import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Chip, Grid, Button, CircularProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { APIURL } from "../../config";
import ComplianceDetails from "./ComplianceDetails";

function KpiCard({ label, value }) {
    return (
        <Paper sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600, mt: 0.5 }}>
                {value === null ? "—" : value}
            </Typography>
        </Paper>
    );
}

function verdictLabel(row) {
    const verdict = row.llm_verdict;
    if (!verdict || verdict.justified === null || verdict.justified === undefined) return "No Verdict";
    return verdict.justified ? "Justified" : "Not Justified";
}

function verdictColor(row) {
    const verdict = row.llm_verdict;
    if (!verdict || verdict.justified === null || verdict.justified === undefined) return "default";
    return verdict.justified ? "success" : "error";
}

const columns = [
    { field: "cus_id", headerName: "Customer ID", width: 120 },
    {
        field: "gap_type",
        headerName: "Type",
        width: 120,
        valueFormatter: (value) => (value === "prior_auth" ? "Prior Auth" : "Claim"),
    },
    {
        field: "reference",
        headerName: "Reference",
        width: 130,
        valueGetter: (value, row) => row.claim_id || row.prior_auth_id || "—",
    },
    {
        field: "issue_codes",
        headerName: "Issues",
        flex: 1.4,
        minWidth: 220,
        renderCell: (params) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
                {(params.value || []).map((code) => (
                    <Chip key={code} label={code.replaceAll("_", " ")} size="small" color="warning" />
                ))}
            </Box>
        ),
    },
    {
        field: "verdict",
        headerName: "LLM Verdict",
        width: 150,
        valueGetter: (value, row) => verdictLabel(row),
        renderCell: (params) => (
            <Chip label={params.value} size="small" color={verdictColor(params.row)} />
        ),
    },
    {
        field: "swept_at",
        headerName: "Swept At",
        width: 180,
        valueFormatter: (value) => (value ? new Date(value).toLocaleString() : "—"),
    },
];

export default function ComplianceDashboard(props) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    // TESTING ONLY -- state backing the manual "Run Sweep Now" button below.
    // Delete sweeping, sweepMessage, runSweepNow, and the button JSX before
    // merge once the nightly cron (index.js) is uncommented.
    const [sweeping, setSweeping] = useState(false);
    const [sweepMessage, setSweepMessage] = useState(null);

    const fetchHistory = () => {
        setLoading(true);
        axios.get(APIURL + "/compliance/history", { params: { limit: 500 } })
            .then((response) => {
                setRows(response.data?.records || []);
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchHistory();
    }, []);

    // TESTING ONLY -- see routes/compliance.js POST /run-now and
    // helpers/qaSweepJob.js runSweepNowHandler. Remove before merge.
    const runSweepNow = () => {
        setSweeping(true);
        setSweepMessage(null);
        axios.post(APIURL + "/compliance/run-now")
            .then((response) => {
                const stats = response.data?.stats || {};
                setSweepMessage(`Sweep complete — ${stats.recordsWritten || 0} record(s) written.`);
                fetchHistory();
            })
            .catch((error) => {
                console.log(error);
                setSweepMessage(error.response?.data?.message || "Sweep failed, see console.");
            })
            .finally(() => {
                setSweeping(false);
            });
    };

    const claimsWithIssues = rows.filter((row) => row.gap_type === "claim").length;
    const authsWithIssues = rows.filter((row) => row.gap_type === "prior_auth").length;
    const notJustified = rows.filter((row) => row.llm_verdict && row.llm_verdict.justified === false).length;

    return (
        <Box sx={{ pt: 1, pb: 1, height: "calc(100vh - 150px)", display: "flex", flexDirection: "column", gap: 1 }}>
            <Grid container spacing={1}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <KpiCard label="Claim Gaps" value={claimsWithIssues} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <KpiCard label="Prior Auth Gaps" value={authsWithIssues} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <KpiCard label="Not Justified by Narrative" value={notJustified} />
                </Grid>
            </Grid>

            <Paper sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Compliance Gap Sweep
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                            Results from the nightly QA sweep — deterministic billing exceptions paired with the LLM's
                            judgement on whether the visit narrative justifies what was billed. Click a row for detail.
                        </Typography>
                    </Box>
                    {/* TESTING ONLY -- runs the sweep on demand instead of waiting for the
                        3am cron. Delete this button (and sweeping/sweepMessage/runSweepNow
                        above) before merge; see index.js for the matching cron re-enable. */}
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={runSweepNow}
                        disabled={sweeping}
                        startIcon={sweeping ? <CircularProgress size={14} /> : null}
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        {sweeping ? "Sweeping…" : "Run Sweep Now (Test)"}
                    </Button>
                </Box>
                {sweepMessage && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                        {sweepMessage}
                    </Typography>
                )}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.id}
                        loading={loading}
                        density="compact"
                        disableRowSelectionOnClick
                        onRowClick={(params) => setSelected(params.row)}
                        getRowHeight={() => "auto"}
                    />
                </Box>
            </Paper>

            <ComplianceDetails open={!!selected} onClose={() => setSelected(null)} record={selected} />
        </Box>
    );
}
